/**
 * Murphy Door Configurator - Selection Dependency Coordinator
 *
 * PURPOSE: keeps a dependent category in step with its parent's "none"
 * choice, in EVERY experience. Set Top Cabinet Doors to "No Top Cabinet
 * Doors" and Top Knobs & Pulls follows to "No Knobs or Pulls"; put real
 * cabinet doors back and the knobs return to unanswered so the customer
 * picks one.
 *
 * THE BUG THIS EXISTS TO FIX (Daniel, 2026-08-20, live on dual_curated):
 * "the knobs are in fact making it to the cart even if we dont add the
 * cabinet doors to them." The rule already existed
 * (CONSTANTS.SELECTION_VISIBILITY_DEPS, complete with the exact fallback
 * variant title) but its ONLY consumer was
 * md-config-bl-gate-card-coordinator.js, which fires off
 * business:gate_answer_changed. Gate cards are apo_style machinery, so in
 * classic / dual_full_default / dual_curated_default nothing ever applied
 * the fallback -- the customer's real knob stayed applied and was billed for
 * hardware with no door to mount it on. md-config-bl-cart-manager.js has no
 * awareness of either dependency map, so nothing downstream caught it
 * either.
 *
 * This module hangs the same rule off selection:applied instead, which is
 * mode-independent. md-config-bl-image-layer-coordinator.js already ran this
 * exact detection loop off the same event to drop the knob IMAGE layer -- so
 * the picture was right and only the selection was wrong.
 *
 * 🚨 WHY PROVENANCE, NOT JUST THE VARIANT ID: the reset direction cannot
 * reuse gate-card-coordinator's isCurrentlyAutoAppliedKnobsFallback(). That
 * predicate is named "auto applied" but only asks "is the applied variant
 * the fallback", which it gets away with because in apo_style the dependent
 * card is HIDDEN when it fires, so the customer could not have chosen it.
 * Here the card is VISIBLE in classic/dual and "No Knobs or Pulls" is a
 * legitimate thing to pick on purpose. Resetting on variant identity alone
 * would silently delete a real choice. So the reset also requires provenance
 * !== 'user' (md-config-core-state-manager.js selectionProvenance, written
 * in the conductor's APPLY_SELECTION path from data.source via
 * _mapSourceToProvenance).
 *
 * PHASE: 2 (Event-Driven CCP - Strict Enforcement)
 * MODULE TYPE: Business Logic (bl-)
 * GOLDEN STANDARD REFERENCE: md-config-bl-shelf-mirror-coordinator.js
 *
 * CCP COMPLIANCE:
 * - Event-driven coordination ONLY (no direct BL/cart-manager calls)
 * - PPD-First data access via conductor delegation
 *   (_getSelectionVisibilityDeps, getVirtualizedProductsForCategory,
 *   getAppliedSelectionsMap, getSelectionProvenance)
 * - Publishes business:cascade_apply_gate_defaults -- consumed by the
 *   EXISTING, unmodified bulk-apply funnel in
 *   md-config-bl-cascade-auto-selection-coordinator.js, and
 *   selection:user_unapply_requested for the reset (composition, not
 *   duplication)
 * - Bootstrap barrier pattern for race protection (Golden Pattern #9)
 *
 * DATA FLOW:
 * selection:applied (categoryId is some dependent's dependsOnCategory)
 *   -> applied product handle === hiddenWhenProductHandle?
 *        YES -> dependent already on the fallback? -> no-op (this is what
 *                stops apo_style double-applying alongside the gate-card
 *                path, which is why that path did not need removing)
 *             -> else resolveVariantByTitle(dependent, fallback title)
 *                -> business:cascade_apply_gate_defaults -> (existing funnel)
 *        NO  -> dependent currently on the fallback AND provenance !== 'user'?
 *                -> selection:user_unapply_requested -> (existing handler)
 */
(function (window) {
  'use strict';

  window.MurphyDoorConfigurator = window.MurphyDoorConfigurator || {};
  const CoreEventBus = window.MurphyDoorConfigurator.CoreEventBus;
  const CONSTANTS = window.MurphyDoorConfigurator.CONSTANTS;

  const isDev = () =>
    window.MurphyDoorConfigurator?.utils?.isDevelopment?.() ?? false;

  // ============================================================================
  // PATTERN 6: BOOTSTRAP RUNTIME PROTECTION
  // ============================================================================
  let bootstrapComplete = false;
  let pendingCalls = [];

  // PATTERN 15: CLEANUP ARRAY
  let cleanupHandlers = [];

  // The context string this module stamps on its applies. Becomes
  // APPLY_SELECTION.source in the bulk-apply funnel
  // (md-config-bl-cascade-auto-selection-coordinator.js, `source: bulkContext`),
  // which the conductor maps to provenance 'auto_apply' because the funnel
  // also sets auto_applied: true. That is what makes the reset below able to
  // tell our apply apart from a customer's own.
  const APPLY_CONTEXT = 'selection_dependency';

  /**
   * Resolves a category's single composite product's variant by TITLE.
   * Duplicated from md-config-bl-gate-card-coordinator.js's helper of the
   * same name/shape -- CCP forbids direct module-to-module calls, so a small
   * shared utility like this is duplicated per-consumer rather than imported
   * (md-config-bl-shelf-mirror-coordinator.js does the same).
   * @param {Object} conductor
   * @param {string} categoryName
   * @param {string} variantTitle
   * @returns {{productId: string, variantId: string}|null}
   */
  function resolveVariantByTitle(conductor, categoryName, variantTitle) {
    if (!conductor || !variantTitle) return null;
    try {
      const products =
        conductor.getVirtualizedProductsForCategory(categoryName) || [];
      const product = products[0];
      const variant =
        product && product.variants
          ? product.variants.find((v) => v && v.title === variantTitle)
          : null;
      if (!product || !variant) {
        isDev() &&
          console.warn(
            '[SelectionDependencyCoordinator] Could not resolve variant "' +
              variantTitle +
              '" in category "' +
              categoryName +
              '" -- leaving it unapplied rather than guessing.'
          );
        return null;
      }
      return { productId: product.id, variantId: variant.id };
    } catch (error) {
      isDev() &&
        console.error(
          '[SelectionDependencyCoordinator] resolveVariantByTitle failed:',
          error
        );
      return null;
    }
  }

  /**
   * The handle of the product currently applied to a category, or null.
   * @param {Object} conductor
   * @param {string} categoryName
   * @returns {string|null}
   */
  function appliedProductHandle(conductor, categoryName) {
    try {
      const appliedMap = conductor.getAppliedSelectionsMap
        ? conductor.getAppliedSelectionsMap()
        : null;
      const applied = appliedMap ? appliedMap.get(categoryName) : null;
      if (!applied || !applied.productId) return null;
      const products =
        conductor.getVirtualizedProductsForCategory(categoryName) || [];
      const product = products.find((p) => p && p.id === applied.productId);
      return product ? product.handle : null;
    } catch (error) {
      isDev() &&
        console.error(
          '[SelectionDependencyCoordinator] appliedProductHandle failed:',
          error
        );
      return null;
    }
  }

  /**
   * True if categoryName's CURRENT applied selection is the fallback variant.
   * Identity only -- says nothing about who chose it. Callers that act
   * destructively must also check provenance; see the header note.
   * @param {Object} conductor
   * @param {string} categoryName
   * @param {string} variantTitle
   * @returns {boolean}
   */
  function isFallbackApplied(conductor, categoryName, variantTitle) {
    if (!conductor || !variantTitle) return false;
    try {
      const appliedMap = conductor.getAppliedSelectionsMap
        ? conductor.getAppliedSelectionsMap()
        : null;
      const applied = appliedMap ? appliedMap.get(categoryName) : null;
      if (!applied || applied.variantId == null) return false;
      const products =
        conductor.getVirtualizedProductsForCategory(categoryName) || [];
      const product = products[0];
      const variant =
        product && product.variants
          ? product.variants.find((v) => v && v.title === variantTitle)
          : null;
      return !!(variant && String(variant.id) === String(applied.variantId));
    } catch (error) {
      isDev() &&
        console.error(
          '[SelectionDependencyCoordinator] isFallbackApplied failed:',
          error
        );
      return false;
    }
  }

  /**
   * 🚨 THE PREDICATE THIS MODULE TURNS ON. Whether the dependent's current
   * fallback selection is ours to undo.
   *
   * Returns false whenever provenance cannot be read, which is the deliberate
   * safe direction: leaving a knob applied costs the customer a visible line
   * item they can remove, while a wrong reset silently deletes a choice they
   * made and (because knobs are a required category) drops them back below
   * 100% with no explanation.
   *
   * 'url' counts as ours. A restored config that carried the fallback almost
   * always got it from a previous auto-apply, and the failure mode if we are
   * wrong is the honest one -- a visible unanswered card the customer can
   * re-pick -- not a silent substitution.
   * @param {Object} conductor
   * @param {string} categoryName
   * @returns {boolean}
   */
  function isOursToReset(conductor, categoryName) {
    if (!conductor || typeof conductor.getSelectionProvenance !== 'function') {
      return false;
    }
    try {
      const provenance = conductor.getSelectionProvenance(categoryName);
      if (!provenance) return false;
      return provenance !== 'user';
    } catch (error) {
      isDev() &&
        console.error(
          '[SelectionDependencyCoordinator] isOursToReset failed:',
          error
        );
      return false;
    }
  }

  /**
   * Handles SELECTION_APPLIED. No-ops unless eventData.categoryId is the
   * PARENT of at least one dependent category -- every other selection passes
   * straight through untouched.
   * @param {Object} eventData - {categoryId, productId, variantId, ...}
   */
  function handleSelectionApplied(eventData) {
    if (!bootstrapComplete) {
      pendingCalls.push({ fn: handleSelectionApplied, args: [eventData] });
      return;
    }
    if (!eventData || !eventData.categoryId) return;

    const conductor = window.MurphyDoorConfigurator.conductor;
    if (!conductor) return;

    const deps =
      (typeof conductor._getSelectionVisibilityDeps === 'function'
        ? conductor._getSelectionVisibilityDeps()
        : null) ||
      CONSTANTS.SELECTION_VISIBILITY_DEPS ||
      {};

    const parentHandle = appliedProductHandle(conductor, eventData.categoryId);

    Object.keys(deps).forEach((dependentCategory) => {
      const rule = deps[dependentCategory];
      if (!rule || rule.dependsOnCategory !== eventData.categoryId) return;
      if (!rule.autoApplyVariantTitleOnHide) return;

      const parentIsNone =
        !!parentHandle && parentHandle === rule.hiddenWhenProductHandle;

      const fallbackApplied = isFallbackApplied(
        conductor,
        dependentCategory,
        rule.autoApplyVariantTitleOnHide
      );

      if (parentIsNone) {
        // Idempotency guard. In apo_style the gate-card path has usually
        // already applied this in the same batch; re-applying would be a
        // redundant state write and a second SELECTION_APPLIED. Skipping is
        // also what lets that path stay in place untouched.
        if (fallbackApplied) return;

        const resolved = resolveVariantByTitle(
          conductor,
          dependentCategory,
          rule.autoApplyVariantTitleOnHide
        );
        if (!resolved) return;

        isDev() &&
          console.log(
            '[SelectionDependencyCoordinator] "' +
              eventData.categoryId +
              '" set to none -- applying "' +
              rule.autoApplyVariantTitleOnHide +
              '" to "' +
              dependentCategory +
              '"'
          );

        if (CoreEventBus) {
          CoreEventBus.publish(
            CONSTANTS.EVENTS.BUSINESS_CASCADE_APPLY_GATE_DEFAULTS,
            {
              selections: [
                {
                  categoryId: dependentCategory,
                  productId: resolved.productId,
                  variantId: resolved.variantId,
                },
              ],
              context: APPLY_CONTEXT,
              timestamp: Date.now(),
              source: 'SelectionDependencyCoordinator',
            }
          );
        }
        return;
      }

      // Parent is back to a real product. Undo our fallback so the customer
      // is asked for a knob again -- but only if it was ours to undo.
      if (!fallbackApplied) return;
      if (!isOursToReset(conductor, dependentCategory)) {
        isDev() &&
          console.log(
            '[SelectionDependencyCoordinator] Leaving "' +
              dependentCategory +
              '" alone -- the customer chose that themselves.'
          );
        return;
      }

      if (CoreEventBus) {
        CoreEventBus.publish(
          CONSTANTS.EVENTS.SELECTION_USER_UNAPPLY_REQUESTED,
          {
            categoryId: dependentCategory,
            timestamp: Date.now(),
            source: 'SelectionDependencyCoordinator.resetStaleFallback',
          }
        );
      }
    });
  }

  /**
   * Handles SELECTION_USER_UNAPPLY_COMPLETED for a PARENT category.
   *
   * THE GAP THIS CLOSES (Daniel, 2026-08-26, live on classic). The rule above
   * covers "the customer chose No Cabinet Doors". It does not cover "the
   * cabinet doors were taken away from the customer" -- which is what a
   * material swap does when the applied style has no equivalent in the new
   * wood. Measured live: glass-panel cabinets have no TFL equivalent, so a
   * veneer -> TFL swap unapplied both cabinet categories and left both knob
   * categories applied. Hardware billed with no door to mount it on: the exact
   * complaint from 2026-08-20, reached by a different route.
   *
   * apo_style already handles this, in collectGateFallout()
   * (md-config-bl-material-remap-coordinator.js) -- but that whole module
   * returns early on !isApoStyle(), so classic and both duals had no unapply
   * fallout at all. This module is the one that runs in every experience and
   * already owns the dependency map, so the coverage belongs here.
   *
   * DELIBERATELY IGNORES PROVENANCE, unlike the fallback-reset path above.
   * There, leaving a user's knob alone is the safe direction because the
   * cabinet still exists and the knob is still valid. Here the parent is gone,
   * so no knob selection is valid regardless of who made it -- keeping it means
   * charging for it. Matches what collectGateFallout does in apo_style.
   *
   * @param {Object} eventData - {categoryId, timestamp, source}
   */
  function handleSelectionUnapplied(eventData) {
    if (!bootstrapComplete) {
      pendingCalls.push({ fn: handleSelectionUnapplied, args: [eventData] });
      return;
    }
    if (!eventData || !eventData.categoryId) return;

    const conductor = window.MurphyDoorConfigurator.conductor;
    if (!conductor) return;

    const deps =
      (typeof conductor._getSelectionVisibilityDeps === 'function'
        ? conductor._getSelectionVisibilityDeps()
        : null) ||
      CONSTANTS.SELECTION_VISIBILITY_DEPS ||
      {};

    let appliedMap = null;
    try {
      appliedMap = conductor.getAppliedSelectionsMap
        ? conductor.getAppliedSelectionsMap()
        : null;
    } catch (error) {
      isDev() &&
        console.error(
          '[SelectionDependencyCoordinator] unapply sweep could not read state:',
          error
        );
      return;
    }
    if (!appliedMap || typeof appliedMap.get !== 'function') return;

    // Re-read the parent from state rather than trusting the event. Ordering
    // between an unapply and its replacement apply is not guaranteed, and
    // acting on a parent that is already applied again would delete a valid
    // knob.
    const parentStillApplied = appliedMap.get(eventData.categoryId);
    if (parentStillApplied && parentStillApplied.variantId != null) return;

    Object.keys(deps).forEach((dependentCategory) => {
      const rule = deps[dependentCategory];
      if (!rule || rule.dependsOnCategory !== eventData.categoryId) return;

      const current = appliedMap.get(dependentCategory);
      if (!current || current.variantId == null) return;

      isDev() &&
        console.log(
          '[SelectionDependencyCoordinator] "' +
            eventData.categoryId +
            '" was unapplied -- releasing dependent "' +
            dependentCategory +
            '" so it cannot be billed with no parent'
        );

      if (CoreEventBus) {
        CoreEventBus.publish(
          CONSTANTS.EVENTS.SELECTION_USER_UNAPPLY_REQUESTED,
          {
            categoryId: dependentCategory,
            timestamp: Date.now(),
            source: 'SelectionDependencyCoordinator.parentUnapplied',
          }
        );
      }
    });
  }

  // ============================================================================
  // POST-CASCADE SWEEP -- a restored build gets the same rule a click does
  // ============================================================================
  //
  // THE GAP THIS CLOSES (prod, 2026-10-09). A ?config= link carrying "No Top
  // Cabinet Doors" plus a real top knob restored with the knob still applied:
  // tile hidden, +$20.90 on the price, and a "Cranford Knob Honey Bronze" line
  // in /cart.js. handleSelectionApplied only fires when the PARENT is applied,
  // so a knob restored after its door is never re-checked. And when it does
  // fire inside a cascade, the bulk-apply funnel
  // (md-config-bl-cascade-auto-selection-coordinator.js
  // handleSimplifiedDefaultsRequest) DROPS the batch: the cascade lock is held
  // until that coordinator's finally, which runs after COMPLETED is published.
  //
  // So after EVERY cascade completes, re-read the live state once, on a
  // microtask (the bus is synchronous and the cascade coordinator has no await,
  // so the microtask runs after its finally has released the lock), and apply
  // the fallback to any dependent whose parent is a "none" product.
  //
  // Every completion, not only restore contexts (unlike the gate-card
  // coordinator's reconcile, which filters on context): the rule only ever
  // moves ONE way -- parent is none => dependent is the fallback -- and the
  // dependent's tile is hidden whenever that holds, so no click path can have
  // left a different, intended choice there. The reverse (real parent => reset)
  // depends on provenance and stays in handleSelectionApplied.
  //
  // Never replays a payload: a dropped batch (the funnel publishes REJECTED)
  // or a deferred sweep is simply redone by the NEXT completion, which always
  // sweeps and recomputes from the state at the time it runs.
  const MAX_SWEEP_ATTEMPTS = 2;
  const SWEEP_SLOW_MS = 16;
  let cascadeActive = false;
  let sweepScheduled = false;
  // dependent -> {parentVariantId, count}. Counted per parent VARIANT, so a
  // fallback that keeps failing (forbidden, unresolvable) cannot loop forever,
  // while a genuinely new parent choice earns fresh attempts.
  const sweepAttempts = new Map();

  /** Would applying variantId to categoryId break a forbidden rule? */
  function isForbiddenNow(conductor, categoryId, variantId) {
    if (
      !conductor ||
      typeof conductor.validateForbiddenCombination !== 'function' ||
      typeof conductor.getAppliedSelections !== 'function'
    ) {
      return false;
    }
    try {
      const others = Object.assign({}, conductor.getAppliedSelections() || {});
      delete others[categoryId];
      const result = conductor.validateForbiddenCombination(
        String(variantId),
        others
      );
      return !!(result && result.isValid === false);
    } catch (error) {
      return false;
    }
  }

  /**
   * One pass over every dependency rule against the CURRENT applied state.
   * Publishes at most one batch; a no-op when the build already satisfies
   * every rule (which is what makes the sweep reach a fixed point).
   */
  function enforceDependentFallbacks() {
    const startedAt = performance.now();
    try {
      const conductor = window.MurphyDoorConfigurator.conductor;
      if (!conductor || !CoreEventBus) return;
      const appliedMap = conductor.getAppliedSelectionsMap
        ? conductor.getAppliedSelectionsMap()
        : null;
      if (!appliedMap || typeof appliedMap.get !== 'function') return;

      const deps =
        (typeof conductor._getSelectionVisibilityDeps === 'function'
          ? conductor._getSelectionVisibilityDeps()
          : null) ||
        CONSTANTS.SELECTION_VISIBILITY_DEPS ||
        {};

      const selections = [];
      Object.keys(deps).forEach((dependentCategory) => {
        const rule = deps[dependentCategory];
        if (!rule || !rule.autoApplyVariantTitleOnHide) return;

        const parent = appliedMap.get(rule.dependsOnCategory);
        if (!parent || parent.variantId == null) return;
        const parentHandle = appliedProductHandle(
          conductor,
          rule.dependsOnCategory
        );
        if (!parentHandle || parentHandle !== rule.hiddenWhenProductHandle) {
          return;
        }
        if (
          isFallbackApplied(
            conductor,
            dependentCategory,
            rule.autoApplyVariantTitleOnHide
          )
        ) {
          sweepAttempts.delete(dependentCategory);
          return;
        }

        const parentKey = String(parent.variantId);
        let attempts = sweepAttempts.get(dependentCategory);
        if (!attempts || attempts.parentVariantId !== parentKey) {
          attempts = { parentVariantId: parentKey, count: 0 };
        }
        if (attempts.count >= MAX_SWEEP_ATTEMPTS) {
          isDev() &&
            console.error(
              '[SelectionDependencyCoordinator] Sweep gave up on "' +
                dependentCategory +
                '" after ' +
                MAX_SWEEP_ATTEMPTS +
                ' attempts -- its fallback is not being applied.'
            );
          return;
        }

        const resolved = resolveVariantByTitle(
          conductor,
          dependentCategory,
          rule.autoApplyVariantTitleOnHide
        );
        if (!resolved) return;
        if (isForbiddenNow(conductor, dependentCategory, resolved.variantId)) {
          isDev() &&
            console.warn(
              '[SelectionDependencyCoordinator] "' +
                rule.autoApplyVariantTitleOnHide +
                '" is forbidden for "' +
                dependentCategory +
                '" in this build -- not applying it.'
            );
          return;
        }

        attempts.count++;
        sweepAttempts.set(dependentCategory, attempts);
        selections.push({
          categoryId: dependentCategory,
          productId: resolved.productId,
          variantId: resolved.variantId,
        });
      });

      if (selections.length === 0) return;
      isDev() &&
        console.log(
          '[SelectionDependencyCoordinator] Post-cascade sweep applying fallbacks to: ' +
            selections.map((sel) => sel.categoryId).join(', ')
        );
      CoreEventBus.publish(
        CONSTANTS.EVENTS.BUSINESS_CASCADE_APPLY_GATE_DEFAULTS,
        {
          selections: selections,
          context: APPLY_CONTEXT,
          timestamp: Date.now(),
          source: 'SelectionDependencyCoordinator.sweep',
        }
      );
    } catch (error) {
      isDev() &&
        console.error(
          '[SelectionDependencyCoordinator] Post-cascade sweep failed:',
          error
        );
    } finally {
      const elapsed = performance.now() - startedAt;
      if (elapsed > SWEEP_SLOW_MS) {
        isDev() &&
          console.warn(
            '[SelectionDependencyCoordinator] Post-cascade sweep took ' +
              elapsed.toFixed(1) +
              'ms'
          );
      }
    }
  }

  /**
   * Coalesce every request in one turn into a single sweep, on a microtask.
   * If a new cascade holds the lock by then, wait for ITS completion.
   */
  function requestDependencySweep() {
    if (sweepScheduled) return;
    sweepScheduled = true;
    Promise.resolve().then(() => {
      sweepScheduled = false;
      // A new cascade took the lock first: its own completion sweeps.
      if (cascadeActive) return;
      enforceDependentFallbacks();
    });
  }

  function handleCascadeStarted() {
    cascadeActive = true;
  }

  function handleCascadeFinished() {
    // Released first, before anything else looks at it.
    cascadeActive = false;
    if (!bootstrapComplete) return;
    requestDependencySweep();
  }


  // ============================================================================
  // BOOTSTRAP SUBSCRIPTION (MODULE LOAD TIME - RACE PROTECTION, Golden Pattern #9)
  // ============================================================================
  if (CoreEventBus) {
    const unsubBootstrap = CoreEventBus.subscribe(
      CONSTANTS.EVENTS.SYSTEM_STATE_FROM_PPD_READY,
      () => {
        bootstrapComplete = true;
        if (pendingCalls.length > 0) {
          isDev() &&
            console.log(
              '[SelectionDependencyCoordinator] Processing ' +
                pendingCalls.length +
                ' queued calls'
            );
          pendingCalls.forEach((call) => {
            try {
              call.fn.apply(null, call.args);
            } catch (error) {
              isDev() &&
                console.error(
                  '[SelectionDependencyCoordinator] Error processing queued call:',
                  error
                );
            }
          });
          pendingCalls = [];
        }
      }
    );
    cleanupHandlers.push(unsubBootstrap);

    const unsubSelectionApplied = CoreEventBus.subscribe(
      CONSTANTS.EVENTS.SELECTION_APPLIED,
      handleSelectionApplied
    );
    cleanupHandlers.push(unsubSelectionApplied);

    // The unapply half of the same dependency. Without it a parent that is
    // taken away (material swap, forbidden-rule sweep) leaves its dependent
    // applied and billable -- see handleSelectionUnapplied.
    const unsubSelectionUnapplied = CoreEventBus.subscribe(
      CONSTANTS.EVENTS.SELECTION_USER_UNAPPLY_COMPLETED,
      handleSelectionUnapplied
    );
    cleanupHandlers.push(unsubSelectionUnapplied);

    // Post-cascade sweep: track the cascade lock and re-check every rule once
    // each cascade (restore, preset, gate batch, curated defaults) finishes.
    cleanupHandlers.push(
      CoreEventBus.subscribe(
        CONSTANTS.EVENTS.BUSINESS_CASCADE_AUTO_SELECTION_STARTED,
        handleCascadeStarted
      ),
      CoreEventBus.subscribe(
        CONSTANTS.EVENTS.BUSINESS_CASCADE_AUTO_SELECTION_COMPLETED,
        handleCascadeFinished
      ),
      CoreEventBus.subscribe(
        CONSTANTS.EVENTS.BUSINESS_CASCADE_AUTO_SELECTION_FAILED,
        handleCascadeFinished
      )
    );
  }

  // ============================================================================
  // PUBLIC API
  // ============================================================================
  window.MurphyDoorConfigurator.selectionDependencyCoordinator = {
    // Exposed for external reference/testing -- not part of the event-driven
    // public contract, callers should publish SELECTION_APPLIED instead.
    handleSelectionApplied: handleSelectionApplied,
    handleSelectionUnapplied: handleSelectionUnapplied,
    enforceDependentFallbacks: enforceDependentFallbacks,
    cleanup: function () {
      cleanupHandlers.forEach((unsub) => {
        try {
          if (typeof unsub === 'function') unsub();
        } catch (error) {
          isDev() &&
            console.error(
              '[SelectionDependencyCoordinator] Cleanup error:',
              error
            );
        }
      });
      cleanupHandlers = [];
    },
    getState: function () {
      return Object.freeze({
        bootstrapComplete: bootstrapComplete,
        pendingCallCount: pendingCalls.length,
        subscriptionCount: cleanupHandlers.length,
        cascadeActive: cascadeActive,
      });
    },
  };

  isDev() && console.log('[SelectionDependencyCoordinator] Module loaded');
})(window);
